/**
 * ErrorBoundary.tsx  ─–  drop-in error wrapper
 * ---------------------------------------------------------------
 * Keeps errors local to the wrapped subtree.
 * - shows a styled inline fallback by default
 * - accepts an optional `fallback` prop for your own UI
 */

import React, { ReactNode, ErrorInfo } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';

/* ------------------------------------------------------------------ */
/* 1. Default fallback element                                        */
/* ------------------------------------------------------------------ */
export function DefaultErrorFallback({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}) {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        bgcolor: theme.palette.error.light,   // light red
        color: theme.palette.common.white,    // white text
        borderRadius: 1,                      // 8px radius
        maxWidth: '100%',
      })}
    >
      {/* one-liner + tooltip for the full text */}
      <Tooltip title={error.message}>
        <Typography
          variant="body2"
          noWrap
          sx={{ flex: 1, fontWeight: 500 }}
        >
          {error.message}
        </Typography>
      </Tooltip>

      {/* retry / reset button */}
      <Tooltip title="Retry">
        <IconButton
          onClick={reset}
          size="small"
          sx={{ color: 'inherit', p: 0.5, '&:hover': { opacity: 0.8 } }}
        >
          <ReplayIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* 2. The ErrorBoundary class                                         */
/* ------------------------------------------------------------------ */
export class ErrorBoundary extends React.Component<
  {
    children: ReactNode;
    /** custom fallback component – receives `{ error, reset }` */
    fallback?: ReactNode;
    /** called after a reset                                        */
    onReset?: () => void;
  },
  { error: Error | null }
> {
  state = { error: null };

  static getDerivedStateFromError(error: Error) {
    // put error into state so the next render shows fallback
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // optional side-effects (Sentry etc.)
    console.error('[ErrorBoundary] Caught:', error, info);
  }

  reset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render() {
    const { error } = this.state;

    if (error) {
      const { fallback } = this.props;

      // if consumer provided a fallback component -> use it
      if (fallback) {
        return typeof fallback === 'function'
          ? (fallback as any)({ error, reset: this.reset })
          : fallback;
      }

      // otherwise use the default pill
      return <DefaultErrorFallback error={error} reset={this.reset} />;
    }

    // no error – render children as usual
    return this.props.children;
  }
}
